import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Paper,
    Divider,
    Grid,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import ConversationBlock from './blocktypes/ConversationBlock';
import QuestionnaireBlock from './blocktypes/QuestionnaireBlock';
import StimuliBlock from './blocktypes/StimuliBlock';

// Get API base URL from environment variable
const API_URL = process.env.REACT_APP_API_ROOT || 'http://localhost:4000';

// LEGACY experiment configuration UI (preserved for backward compatibility)
// New multi-level builder lives under builder/ directory.
function ConfigConversationLegacy({ onSubmit }) {
    const [agents, setAgents] = useState([]); // DB agents
    const [experimentBlocks, setExperimentBlocks] = useState([]); // Experiment blocks. block.agents(.agent_id) is a list of agents
    const [availableScales, setAvailableScales] = useState([]);
    const { getAccessTokenSilently } = useAuth0();

    useEffect(() => {
        const fetchScales = async () => {
            console.log('Fetching scales...');
            try {
                const token = await getAccessTokenSilently();
                const response = await axios.get(`${API_URL}/api/simulations/operators`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setAvailableScales(response.data);
            } catch (error) {
                console.error('Error fetching scales:', error);
            }
        };

        fetchScales();
    }, [getAccessTokenSilently]);

    useEffect(() => {
        axios
            .get(`${API_URL}/api/agents`)

            .then((response) => {
                setAgents(response.data);
            })
            .catch((error) => {
                console.error('Error fetching agents:', error);
            });
    }, []);

    const formatAgentsInBlocks = (blocks) => {

        const updatedBlocks = blocks.map((block) => {
            if (block.type === 'conversation') {
                console.log('updating block:', block);

                const updatedBlock = {
                    ...block,
                    agents: block.agents.map((agent) => {
                        const agentData = { ...agent, ...agents.find((a) => a.id === agent.agent_id) };
                        delete agentData.agent_id // Remove agent_id from the object

                        if (!agentData) {
                            console.error(`Agent with ID ${agent.agent_id} not found.`);
                            return null;
                        }
                        console.log('found agent:', agentData);
                        return agentData;
                    }).filter(Boolean), // Remove null agents
                };

                console.log('updated block:', updatedBlock);
                return updatedBlock;
            }
            return block;
        });
        console.log('Formatted blocks:', updatedBlocks);
        return updatedBlocks;
    };

    const addBlock = (blockType) => {
        const newBlock = {
            type: blockType,
            blockNumber: experimentBlocks.length + 1,
            randomize: false
        };

        if (blockType === 'conversation') {
            newBlock.agents = [{
                agent_id: ''
            }];
            newBlock.end_condition = 'responseTimeout';
            newBlock.task_description = `You are having a time-limited conversation with a user. {timer_info}\n\nUse the conversation history to build your response to the user:\n\n{history}\n\nRespond to the user's message: {user_message}`;
            newBlock.task_expected_output = `Your output should be a relevant, accurate, and direct response.\n\n{agent_end}`;
        } else if (blockType === 'questionnaire') {
            newBlock.title = '';
            newBlock.instructions = '';
            newBlock.questions = [{
                type: 'likert',
                text: '',
                options: ['1', '2', '3', '4', '5', '6', '7'],
                required: true
            }];
        } else if (blockType === 'stimuli') {
            // Comprehensive stimuli block
            newBlock.stimuliSet = null;
            newBlock.stimuliSetId = null;
            newBlock.questions = [];
            newBlock.introduction = '';
            newBlock.outroduction = '';
            newBlock.settings = {
                randomizeOrder: false,
                showProgress: true,
                allowSkip: false,
                minViewTime: 0,
                autoAdvance: false,
                autoAdvanceTime: 0
            };
        } else if (blockType === 'scale') {
            newBlock.title = '';
            newBlock.scale = {
                id: '',
                name: '',
                items: [],
                response_model: {}
            };
        }

        setExperimentBlocks([...experimentBlocks, newBlock]);
    };

    const updateBlock = (index, updatedBlock) => {
        console.log('Updating block at index:', index, 'with:', updatedBlock);
        const updatedBlocks = experimentBlocks.map((b, i) => (i === index ? { ...updatedBlock } : b));
        setExperimentBlocks(updatedBlocks);
    };

    const removeBlock = (index) => {
        const updatedBlocks = experimentBlocks.filter((_, i) => i !== index);
        const renumberedBlocks = updatedBlocks.map((block, i) => ({
            ...block,
            blockNumber: i + 1
        }));
        setExperimentBlocks(renumberedBlocks);
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(experimentBlocks);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        const updatedBlocks = items.map((block, index) => ({
            ...block,
            blockNumber: index + 1
        }));

        setExperimentBlocks(updatedBlocks);
    };

    const handleLaunch = async () => {
        const formattedBlocks = formatAgentsInBlocks(experimentBlocks);
        const agentsConfig = [];
        const task = {
            type: 'conversation',
            parameters: { blocks: experimentBlocks.length },
            blocks: []
        };

        formattedBlocks.forEach(async (block, blockIndex) => {
            const blockNumber = blockIndex + 1;

            if (block.type === 'conversation') {
                const numTrials = block.agents.length;

                // Add trials field with agent order
                const trials = block.agents.map((agent, agentIndex) => ({
                    order: agentIndex + 1,
                    agent_id: agent.id,
                    agent_name: agent.name
                }));

                task.blocks.push({
                    block_number: blockNumber,
                    type: 'conversation',
                    num_trials: numTrials,
                    randomize: block.randomize,
                    end_condition: block.end_condition,
                    timeoutDuration: block.timeoutDuration,
                    task_description: block.task_description,
                    task_expected_output: block.task_expected_output,
                    custom_tool_description: block.custom_tool_description,
                    introduction_text: block.introduction_text,
                    outroduction_text: block.outroduction_text,
                    trials // Include trials field
                });

                // Add agents to the agentsConfig array
                block.agents.forEach((agent) => {
                    const agentData = agents.find((a) => a.id === agent.id);
                    if (!agentData) {
                        console.error(`Agent with ID ${agent.id} not found.`);
                        return;
                    }

                    agentsConfig.push({
                        block: blockNumber,
                        id: agent.id,
                        name: agent.name,
                        crew_settings: {
                            role: agentData.role,
                            backstory: agentData.backstory,
                            goal: agentData.goal
                        }
                    });
                });
            } else if (block.type === 'questionnaire') {
                console.log('Handling questionnaire block:', block);
                const numTrials = block.questions.length;

                task.blocks.push({
                    block_number: blockNumber,
                    type: 'questionnaire',
                    title: block.title,
                    instructions: block.instructions,
                    num_trials: numTrials,
                    randomize: block.randomize,
                    questions: block.questions,
                    introduction_text: block.introduction_text,
                    outroduction_text: block.outroduction_text,
                });

            } else if (block.type === 'scale') {
                console.log("Handling scale block:", block);
                const numTrials = block.questions.length;
                const scaleData = availableScales.find((scale) => scale._id === block.scale._id);
                if (!scaleData) {
                    console.error(`Scale with ID ${block.scale._id} not found.`);
                    return;
                }

                task.blocks.push({
                    block_number: blockNumber,
                    type: 'scale',
                    title: block.title || scaleData.name,
                    instructions: block.instructions,
                    num_trials: numTrials,
                    randomize: block.randomize,
                    scale: {
                        _id: scaleData._id,
                        id: scaleData.id,
                        name: scaleData.name,
                        author: scaleData.author
                    },
                    questions: scaleData.items,
                    response_model: scaleData.response_model,
                    prompt_templates: scaleData.prompt_templates,
                    introduction_text: block.introduction_text,
                    outroduction_text: block.outroduction_text,
                });
            } else if (block.type === 'stimuli') {
                console.log('Handling stimuli block:', block);
                
                task.blocks.push({
                    block_number: blockNumber,
                    type: 'stimuli',
                    stimuliSetId: block.stimuliSetId,
                    stimuliSet: block.stimuliSet,
                    questions: block.questions || [],
                    settings: block.settings || {},
                    introduction_text: block.introduction,
                    outroduction_text: block.outroduction,
                });
            }
        });

        const configData = {
            task,
            agents: agentsConfig
        };

        onSubmit(configData);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
                <Grid item xs={3}>
                    <Paper sx={{ p: 2, height: '100%' }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Block Library</Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={() => addBlock('conversation')}
                                fullWidth
                            >
                                + Conversation
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => addBlock('questionnaire')}
                                fullWidth
                            >
                                + Questionnaire
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => addBlock('stimuli')}
                                fullWidth
                            >
                                + Stimuli
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={9}>
                    <Paper sx={{ p: 2, minHeight: '500px' }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Experiment Flow</Typography>
                        <Divider sx={{ mb: 2 }} />

                        {experimentBlocks.length === 0 ? (
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '200px',
                                border: '2px dashed #ccc',
                                borderRadius: '4px'
                            }}>
                                <Typography color="textSecondary">
                                    Drag blocks here to design your experiment
                                </Typography>
                            </Box>
                        ) : (
                            <DragDropContext onDragEnd={onDragEnd}>
                                <Droppable droppableId="experiment-blocks">
                                    {(provided) => (
                                        <Box
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                        >
                                            {experimentBlocks.map((block, index) => (

                                                <Draggable
                                                    key={`block-${index}`}
                                                    draggableId={`block-${index}`}
                                                    index={index}
                                                >
                                                    {(provided) => (
                                                        <Box
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <Box {...provided.dragHandleProps} sx={{ mr: 1 }}>
                                                                    <DragIndicatorIcon />
                                                                </Box>
                                                                <Box sx={{ flexGrow: 1 }}>
                                                                    {block.type === 'conversation' && (
                                                                        <ConversationBlock
                                                                            block={block}
                                                                            agents={agents}
                                                                            index={index}
                                                                            updateBlock={updateBlock}
                                                                            removeBlock={removeBlock}
                                                                        />
                                                                    )}
                                                                    {block.type === 'questionnaire' && (
                                                                        <QuestionnaireBlock
                                                                            block={block}
                                                                            index={index}
                                                                            availableScales={availableScales}
                                                                            updateBlock={updateBlock}
                                                                            removeBlock={removeBlock}
                                                                        />
                                                                    )}
                                                                    {block.type === 'scale' && (
                                                                        <QuestionnaireBlock
                                                                            block={block}
                                                                            index={index}
                                                                            availableScales={availableScales}
                                                                            updateBlock={updateBlock}
                                                                            removeBlock={removeBlock}
                                                                        />
                                                                    )}
                                                                    {console.log('block type:', block.type)}
                                                                    {console.log('available scales:', availableScales)}
                                                                    {block.type === 'stimuli' && (
                                                                        <StimuliBlock
                                                                            block={block}
                                                                            index={index}
                                                                            updateBlock={updateBlock}
                                                                            removeBlock={removeBlock}
                                                                        />
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </Box>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleLaunch}
                    disabled={experimentBlocks.length === 0}
                >
                    Launch Experiment
                </Button>
            </Box>
        </Box>
    );
}

export default ConfigConversationLegacy;